import React, { useState, useEffect } from "react";
import { X, Pencil } from "lucide-react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

const TeacherModal = ({
  isModalOpen,
  isEdit,
  onSubmit,
  handleSubmit,
  setIsModalOpen,
  register,
  password,
  currentPicture, // Pass the current picture when editing
  campusClasses,
  selectedClasses,
  uploadType,
  reset,
  onModalClose,
  campuses,
  selectedDays,
  setSelectedDays,
  watch, // Add watch to props
}) => {
  const { t } = useTranslation();
  const {
    setError,
    formState: { errors },
  } = useForm();
  const [newPicture, setNewPicture] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [addedClasses, setAddedClasses] = useState([]);
  const [bulkFile, setBulkFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const availableDays = [
    t("Monday"),
    t("Tuesday"), 
    t("Wednesday"),
    t("Thursday"),
    t("Friday"),
    t("Saturday"),
  ];
  const fileInputRef = React.useRef(null);

  // Watch for campusId changes
  const campusId = watch("campusId");

  useEffect(() => {
    if (isEdit && Array.isArray(selectedClasses)) {
      const mappedClasses = selectedClasses.map((classItem) => {
        // Find the class in filteredClasses to get its sections
        const selectedClass = filteredClasses?.find(cls => cls.id === classItem.classId);
        const classSections = selectedClass?.sections || [];

        return {
          classId: classItem.classId,
          classDetails: classItem.subjects?.map((subject) => {
            const selectedSection = classSections.find(
              (section) => section.id === subject.sectionId
            );

            return {
              sectionId: subject.sectionId,
              subjectId: subject.subjectId,
              filteredSubjects: selectedSection?.subjects || [],
            };
          }),
          filteredSections: classSections,
        };
      });

      setAddedClasses(mappedClasses);
    } else {
      setAddedClasses([]);
    }
  }, [isModalOpen, isEdit, selectedClasses, filteredClasses]);

  // Update sections when campusId changes
  useEffect(() => {
    if (campusId) {
      const filteredClasses = campusClasses?.find(campusItem => campusItem.id === campusId)?.classes || [];
      setFilteredClasses(filteredClasses);

      // If we're in edit mode and have selected classes, update their sections
      if (isEdit && Array.isArray(selectedClasses)) {
        const updatedClasses = addedClasses.map(cls => {
          const selectedClass = filteredClasses.find(c => c.id === cls.classId);
          return {
            ...cls,
            filteredSections: selectedClass?.sections || [],
          };
        });
        setAddedClasses(updatedClasses);
      }
    }
  }, [campusId, campusClasses, isEdit, selectedClasses]);

  useEffect(() => {
    if (isEdit) {
      setSelectedFile(null); // Clear file on edit to avoid conflicts
      setNewPicture(currentPicture);
    }
  }, [isModalOpen, isEdit, currentPicture]);

  const handlePictureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file); // Store the file
      const imageUrl = URL.createObjectURL(file); // Preview the image
      setNewPicture(imageUrl);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleFormSubmit = (data) => {
    if (!isEdit && data.password !== data.confirmPassword) {
      return setError("confirmPassword", {
        type: "manual",
        message: t("Password doesn't match"),
      });
    }
    onSubmit({ ...data, addedClasses, selectedDays }, selectedFile); // Include dynamic classes in the submission
  };

  // Add a new class assignment
  const handleAddClass = () => {
    setAddedClasses((prev) => [
      ...prev,
      {
        classId: "",
        classDetails: [],
        filteredSections: [],
        filteredSubjects: [],
      },
    ]);
  };

  // Remove a class assignment
  const handleRemoveClass = (classIndex) => {
    setAddedClasses((prev) => prev.filter((_, idx) => idx !== classIndex));
  };

  // Handle dynamic changes in the selected class
  const handleDynamicClassChange = (classIndex, value) => {
    setAddedClasses((prev) =>
      prev.map((cls, idx) => {
        if (idx === classIndex) {
          const selectedClass = filteredClasses?.find(
            (classItem) => classItem.id === value
          );
          return {
            ...cls,
            classId: value,
            filteredSections: selectedClass?.sections || [],
            classDetails: selectedClass?.sections || [], // Reset sections and subjects
          };
        }
        return cls;
      })
    );
  };

  // Add a section and subject under a class
  const handleAddSectionSubject = (classIndex) => {
    setAddedClasses((prev) =>
      prev.map((cls, idx) =>
        idx === classIndex
          ? {
              ...cls,
              classDetails: [
                ...cls.classDetails,
                { sectionId: "", subjectId: "", filteredSubjects: [] },
              ],
            }
          : cls
      )
    );
  };

  // Remove a section and subject under a class
  const handleRemoveSectionSubject = (classIndex, sectionIndex) => {
    setAddedClasses((prev) =>
      prev.map((cls, idx) =>
        idx === classIndex
          ? {
              ...cls,
              classDetails: cls.classDetails.filter(
                (_, secIdx) => secIdx !== sectionIndex
              ),
            }
          : cls
      )
    );
  };

  // Handle dynamic changes for section and subject
  const handleDynamicSectionChange = (classIndex, sectionIndex, value) => {
    setAddedClasses((prev) =>
      prev.map((cls, idx) => {
        if (idx === classIndex) {
          const selectedSection = cls.filteredSections.find(
            (sec) => sec.id === value
          );
          const updatedClassDetails = cls.classDetails.map((detail, idx) =>
            idx === sectionIndex
              ? {
                  ...detail,
                  sectionId: value,
                  filteredSubjects: selectedSection?.subjects || [],
                  subjectId: "", // Reset subject when section changes
                }
              : detail
          );
          return { ...cls, classDetails: updatedClassDetails };
        }
        return cls;
      })
    );
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setBulkFile(file);
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkFile) {
      toast.error(t("Please upload a file first!"));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const uploadSimulation = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadSimulation);
          setIsUploading(false);
          toast.success(t("File has been uploaded!"));
          setBulkFile(null);
          setIsModalOpen(false);
          onModalClose();
        }
        return prev + 10; // Increment progress
      });
    }, 300); // Adjust interval speed as needed
  };

  const handleClose = () => {
    reset();
    setIsModalOpen(false);
    setUploadProgress(0);
    setBulkFile(null);
    setIsUploading(false);
    if (onModalClose) {
      onModalClose();
    }
  };

  const handleDayToggle = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="px-2 py-1 bg-red-500 text-white rounded"
          >
            <span>
              <X />
            </span>
          </button>
        </div>
        <h2 className="text-xl font-bold mb-4">
          {isEdit ? t("EditTeacher") : t("AddTeacher")}
        </h2>

        <div className="mb-4 flex justify-center">
          <div className="relative inline-block group">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center cursor-pointer">
              {newPicture ? (
                <img
                  src={newPicture}
                  alt={t("ProfilePreview")}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gray-500">{t("Noimage")}</span>
              )}
            </div>
            <button
              type="button"
              className="absolute bottom-1 right-1 bg-white p-1.5 rounded-full shadow-md hover:bg-gray-100 transition-colors"
              onClick={handleImageClick}
            >
              <Pencil className="w-4 h-4 text-gray-700" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePictureChange}
              accept="image/*"
              className="hidden"
              required={!isEdit}
            />
          </div>
        </div>
        {isEdit || uploadType === "single" ? (
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <h1 className="text-lg font-bold">{t("PersonalDetails")}</h1>
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              <div>
                <label className="block">{t("FirstName")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("firstName")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("LastName")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("lastName")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("Gender")}</label>
                <select
                  className="w-full p-2 border rounded"
                  {...register("gender")}
                  required
                >
                  <option value="Male">{t("Male")}</option>
                  <option value="Female">{t("Female")}</option>
                </select>
              </div>
              <div>
                <label className="block">{t("Phone")}</label>
                <input
                  type="tel"
                  className="w-full p-2 border rounded"
                  {...register("phone")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("Location")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("location")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("Email")}</label>
                <input
                  type="email"
                  className="w-full p-2 border rounded"
                  {...register("email")}
                  required
                />
              </div>
              {/* Password */}
              <div>
                <label className="block">{t("Password")}</label>
                <input
                  type="password"
                  {...register("password", {
                    required: !isEdit && t("Password is required"), // Required if not edit mode
                  })}
                  placeholder={t("Password")}
                  className="w-full p-2 border rounded"
                />
                {errors.password && (
                  <p className="text-red-500 text-sm">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block">{t("ConfirmPassword")}</label>
                <input
                  type="password"
                  {...register("confirmPassword", {
                    validate: (value) =>
                      isEdit || value === password || t("Passwords do not match"),
                  })}
                  placeholder={t("ConfirmPassword")}
                  className="w-full p-2 border rounded"
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm">
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block">{t("DOB")}</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  {...register("dob")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("JoiningDate")}</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  {...register("joiningDate")}
                  required
                />
              </div>

              <div>
                <label className="block">{t("CNIC")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("cnic")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("EmployeeCode")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("employeeCode")}
                  required={isEdit}
                  readOnly={!isEdit}
                />
              </div>
              {/* New fields */}
              {isEdit && (
                <div>
                  <label className="block">{t("Status")}</label>
                  <select
                    className="w-full p-2 border rounded"
                    {...register("userStatus")}
                    required
                  >
                    <option value="Active">{t("Active")}</option>
                    <option value="Resigned">{t("Resigned")}</option>
                    <option value="Unpaid Leaves">{t("Unpaid Leaves")}</option>
                    <option value="Promoted">{t("Promoted")}</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block">{t("MaritalStatus")}</label>
                <select
                  className="w-full p-2 border rounded"
                  {...register("maritalStatus")}
                  required
                >
                  <option value="Single">{t("Single")}</option>
                  <option value="Marreid">{t("Married")}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block">{t("SelectCampus")}</label>
              <select
                {...register("campusId", { required: true })}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">{t("SelectCampus")}</option>
                {Array.isArray(campuses) &&
                  campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name} - {campus.location}
                    </option>
                  ))}
              </select>
            </div>
            <h1 className="mt-2 text-lg font-bold">{t("Academic&Experience")}</h1>
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              <div>
                <label className="block">{t("Institute")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("academicInstitute")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("CompletedIn")}</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  {...register("completedYear")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("MajorSubject")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("majorSubject")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("CGPA")}</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  {...register("cgpa")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("Percentage")}</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  {...register("percentage")}
                  required
                />
              </div>
            </div>
            <h1 className="mt-2 text-lg font-bold">{t("ProfessionalExperience")}</h1>
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              <div>
                <label className="block">{t("OrganizationName")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("organizationName")}
                />
              </div>
              <div>
                <label className="block">{t("Designation")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("designation")}
                />
              </div>
              <div>
                <label className="block">{t("Starteddate")}</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  {...register("startedOn")}
                />
              </div>
              <div>
                <label className="block">{t("EndedDate")}</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  {...register("endedOn")}
                />
              </div>
              <div>
                <label className="block">{t("LastSalaryDrawn")}</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  {...register("lastSalaryDrawn")}
                />
              </div>
              <div>
                <label className="block">{t("MainResponsiblities")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("mainResponsibilities")}
                />
              </div>
              <div>
                <label className="block">{t("Other")}</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  {...register("other")}
                />
              </div>
            </div>

            {/* New Availability Section */}
            <h1 className="mt-2 text-lg font-bold">{t("AvailabilityDetails")}</h1>
            <div className="grid grid-cols-2 gap-4 border-b border-gray-200 pb-4">
              <div>
                <label className="block">{t("MaxWeeklyHours")}</label>
                <select
                  className="w-full p-2 border rounded"
                  {...register("maxHoursPerWeek")}
                  required
                >
                  <option value="">{t("SelectHoursRange")}</option>
                  <option value="10">1 to 10</option>
                  <option value="20">10 to 20</option>
                  <option value="30">20 to 30</option>
                  <option value="40">30 to 40</option>
                </select>
              </div>
              <div>
                <label className="block">{t("FromTime")}</label>
                <input
                  type="time"
                  className="w-full p-2 border rounded"
                  {...register("fromTime")}
                  required
                />
              </div>
              <div>
                <label className="block">{t("ToTime")}</label>
                <input
                  type="time"
                  className="w-full p-2 border rounded"
                  {...register("toTime")}
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="block mb-2">{t("AvailableDays")}</label>
                <div className="flex flex-wrap gap-2">
                  {availableDays.map((day) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => handleDayToggle(day)}
                      className={`px-3 py-1 rounded ${
                        selectedDays.includes(day)
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <div>
                <span className="text-base font-semibold">
                  {t("AddClassesforTeacher")}
                </span>
              </div>
              <button
                type="button"
                onClick={handleAddClass}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                <span>+</span> {t("AddClass")}
              </button>
            </div>

            {addedClasses.map((cls, classIndex) => (
              <div
                key={classIndex}
                className="mt-4 border-t border-gray-200 pt-4"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label>{t("Class")}</label>
                    <select
                      value={cls.classId}
                      onChange={(e) =>
                        handleDynamicClassChange(classIndex, e.target.value)
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="">{t("SelectaClass")}</option>
                      {filteredClasses?.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.className}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveClass(classIndex)}
                    className="text-red-500 text-3xl font-bold"
                  >
                    ×
                  </button>
                </div>

                {cls.classDetails.map((section, sectionIndex) => (
                  <div
                    key={sectionIndex}
                    className="mt-4 ml-8 flex items-center gap-4"
                  >
                    <div className="flex-1">
                      <label>{t("Section")}</label>
                      <select
                        value={section.sectionId}
                        onChange={(e) =>
                          handleDynamicSectionChange(
                            classIndex,
                            sectionIndex,
                            e.target.value
                          )
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">{t("SelectaSection")}</option>
                        {cls.filteredSections.map((sec) => (
                          <option key={sec.id} value={sec.id}>
                            {sec.sectionName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label>{t("Subject")}</label>
                      <select
                        value={section.subjectId}
                        onChange={(e) =>
                          setAddedClasses((prev) =>
                            prev.map((cls, idx) =>
                              idx === classIndex
                                ? {
                                    ...cls,
                                    classDetails: cls.classDetails.map(
                                      (detail, sIdx) =>
                                        sIdx === sectionIndex
                                          ? {
                                              ...detail,
                                              subjectId: e.target.value,
                                            }
                                          : detail
                                    ),
                                  }
                                : cls
                            )
                          )
                        }
                        className="w-full p-2 border rounded"
                      >
                        <option value="">{t("SelectaSubject")}</option>
                        {section.filteredSubjects
                          ?.filter(
                            (subj) =>
                              !cls.classDetails.some(
                                (detail) =>
                                  detail.subjectId === subj.id &&
                                  detail !== section
                              )
                          )
                          .map((subj) => (
                            <option key={subj.id} value={subj.id}>
                              {subj.subjectName}
                            </option>
                          ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveSectionSubject(classIndex, sectionIndex)
                      }
                      className="text-red-500 text-3xl font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => handleAddSectionSubject(classIndex)}
                  className="mt-4 text-blue-500 font-bold"
                  disabled={!cls.classId}
                >
                  + {t("AddSectionandSubject")}
                </button>
              </div>
            ))}

            <div className="flex justify-end space-x-4 pt-24">
              <button
                type="submit"
                className="px-4 py-2 bg-[#241763] text-white rounded"
              >
                {isEdit ? t("Update") : t("Add")}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <h1 className="text-lg font-bold">{t("BulkUpload")}</h1>
            <input
              type="file"
              className="w-full p-2 border rounded"
              onChange={handleBulkFileChange}
              accept=".xlsx, .xls"
              disabled={isUploading}
            />
            {isUploading && (
              <div className="mt-4">
                <label className="block font-medium mb-2">
                  {t("UploadProgress")}
                </label>
                <div className="relative w-full h-4 bg-gray-200 rounded">
                  <div
                    className="absolute top-0 left-0 h-4 bg-green-500 rounded"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600">{uploadProgress}%</span>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={handleBulkSubmit}
                className={`px-4 py-2 rounded ${
                  isUploading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-[#241763] text-white"
                }`}
                disabled={isUploading}
              >
                {isUploading ? t("Uploading...") : t("Submit")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherModal;
